import React, { useState, ChangeEvent, FormEvent } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Alert,
  Container,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Email, 
  ArrowBack,
  Send
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForgotPasswordMutation } from '../../api/authApi';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

interface ForgotPasswordProps {}

interface ForgotPasswordForm {
  email: string;
}

export const ForgotPassword: React.FC<ForgotPasswordProps> = () => {
  const navigate = useNavigate();
  const [forgotPassword, { isLoading, error, isSuccess }] = useForgotPasswordMutation();
  const forgotPasswordError = error as FetchBaseQueryError | undefined;
  const [formData, setFormData] = useState<ForgotPasswordForm>({
    email: '',
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: ForgotPasswordForm) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await forgotPassword({ email: formData.email });
    } catch (err) {
      console.error('Forgot password failed:', err);
      if (err && typeof err === 'object' && 'data' in err) {
        console.error('Error details:', (err as any).data);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 6,
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Forgot Password
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: '1.1rem' }}
            >
              Enter your email address and we'll send you a link to reset your password
            </Typography>
          </Box>

          {/* Success Alert */}
          {isSuccess && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                border: '1px solid rgba(34, 197, 94, 0.2)',
              }}
            >
              Password reset link has been sent to your email address. Please check your inbox.
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}
            >
              {typeof forgotPasswordError === 'object' && forgotPasswordError ?
                forgotPasswordError?.status?.toString() || (forgotPasswordError as { data: { message?: string } })?.data?.message || 'An error occurred while sending reset email'
                : 'An error occurred while sending reset email'}
            </Alert>
          )}

          {/* Forgot Password Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                    },
                  },
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading}
              startIcon={<Send />}
              sx={{
                mt: 4,
                mb: 3,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0px 8px 16px rgba(99, 102, 241, 0.3)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #a5b4fc 0%, #67e8f9 100%)',
                },
              }}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          {/* Back to Login */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/auth/login')}
              sx={{
                color: '#6366f1',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}; 